<script>
  import { token } from "./store/token.js";
  import { apiRequest } from "./api/request.js";
  import DatePicker from "./component/date-picker/DatePicker.svelte";
  import { getPlanDisplayName } from "./lib/planDisplay.js";

  let max_signup;
  let max_login;
  let max_platform_occurence;
  let max_platform_os_occurence;
  let signup_data;
  let login_data;
  let dashboard_summary;

  var dateOffset = 365 * 24 * 60 * 60 * 1000; //5 days
  var currentFromDate = new Date();
  currentFromDate.setTime(currentFromDate.getTime() - dateOffset);

  let currentToDate = new Date();

  const onFromDateChange = (d) => {
    currentFromDate = d.detail;
    fetch_data();
  };

  const onToDateChange = (d) => {
    currentToDate = d.detail;
    fetch_data();
  };

  const fetch_data = async () => {
    const opts = {
      from: currentFromDate,
      to: currentToDate,
      env: process.env.ENVIRONMENT,
    };

    var signup_hist_url = new URL(
      //"https://staging.api.control.vollume.com/admin/signup/histogram"
      `${process.env.API_ROOT}/admin/signup/histogram`,
    );
    signup_hist_url.search = new URLSearchParams(opts).toString();

    const signup_hist_response = await fetch(signup_hist_url, {
      headers: {
        Authorization: "Bearer " + $token,
      },
    });

    signup_data = await signup_hist_response.json();

    var login_hist_url = new URL(
      //"https://staging.api.control.vollume.com/admin/login/histogram"
      `${process.env.API_ROOT}/admin/login/histogram`,
    );
    login_hist_url.search = new URLSearchParams(opts).toString();

    const login_hist_response = await fetch(login_hist_url, {
      headers: {
        Authorization: "Bearer " + $token,
      },
    });

    login_data = await login_hist_response.json();

    const summaryParams = new URLSearchParams({
      from: currentFromDate.toISOString(),
      to: currentToDate.toISOString(),
    });
    const summaryUrl = `${process.env.API_ROOT}/admin/dashboard/summary?${summaryParams}`;
    dashboard_summary = await apiRequest(summaryUrl);

    dashboard_summary.platform_list_counts =
      (dashboard_summary.platform_list_counts ?? []).slice().sort((a, b) => {
        return (b.platform_list_count ?? 0) - (a.platform_list_count ?? 0);
      });
    dashboard_summary.platform_os_counts =
      dashboard_summary.platform_os_counts ?? [];
    dashboard_summary.workspace_subscription_counts =
      dashboard_summary.workspace_subscription_counts ?? [];
    dashboard_summary.previous_workspace_subscription_counts =
      dashboard_summary.previous_workspace_subscription_counts ?? [];

    const platformCounts = dashboard_summary.platform_list_counts.map(
      (item) => item.platform_list_count ?? 0,
    );
    max_platform_occurence =
      platformCounts.length > 0 ? Math.max(...platformCounts) : 0;
    const osCounts = dashboard_summary.platform_os_counts.map(
      (item) => item.count ?? 0,
    );
    max_platform_os_occurence =
      osCounts.length > 0 ? Math.max(...osCounts) : 0;

    max_signup = Math.max(
      ...signup_data.records.map((data_point) => data_point.count),
    );
    max_login = Math.max(
      ...login_data.records.map((data_point) => data_point.count),
    );

    return;
  };

  /** Period-over-period % delta: previous !== 0 ? ((current - previous) / previous) * 100 : (current > 0 ? 100 : 0) */
  const periodDelta = (current, previous) => {
    const curr = Number(current) || 0;
    const prev = Number(previous) || 0;
    if (prev !== 0) return ((curr - prev) / prev) * 100;
    return curr > 0 ? 100 : 0;
  };

  /** Previous count and delta for a workspace plan from summary. */
  const getPlanPeriodDelta = (summary, planItem) => {
    const prev = (summary.previous_workspace_subscription_counts || []).find(
      (p) => p.plan_id === planItem.plan_id,
    );
    const prevCount = prev ? prev.count : 0;
    return { prevCount, delta: periodDelta(planItem.count, prevCount) };
  };

  $: signupDelta = dashboard_summary
    ? periodDelta(dashboard_summary.signup_count, dashboard_summary.previous_signup_count)
    : 0;
  $: mauDelta = dashboard_summary
    ? periodDelta(dashboard_summary.mau_count, dashboard_summary.previous_mau_count)
    : 0;
  $: starterCount =
    dashboard_summary != null
      ? (dashboard_summary.starter_user_count ?? dashboard_summary.free_user_count)
      : 0;
  $: previousStarterCount =
    dashboard_summary != null
      ? (dashboard_summary.previous_starter_user_count ??
        dashboard_summary.previous_free_user_count ??
        0)
      : 0;
  $: starterDelta = dashboard_summary ? periodDelta(starterCount, previousStarterCount) : 0;
  $: trialDelta = dashboard_summary
    ? periodDelta(
        dashboard_summary.workspace_trial_count ?? 0,
        dashboard_summary.previous_workspace_trial_count ?? 0,
      )
    : 0;

  const set_date_range = (range_string) => {
    if (range_string == "last_7_days") {
      currentToDate = new Date();
      currentFromDate = new Date();
      currentFromDate.setDate(currentFromDate.getDate() - 7);
    }

    if (range_string == "last_28_days") {
      currentToDate = new Date();
      currentFromDate = new Date();
      currentFromDate.setDate(currentToDate.getDate() - 28);
    }

    if (range_string == "last_90_days") {
      currentToDate = new Date();
      currentFromDate = new Date();
      currentFromDate.setDate(currentToDate.getDate() - 90);
    }

    if (range_string == "last_365_days") {
      currentToDate = new Date();
      currentFromDate = new Date();
      currentFromDate.setDate(currentToDate.getDate() - 365);
    }

    if (range_string == "year_to_date") {
      currentToDate = new Date();
      currentFromDate = new Date(new Date().getFullYear(), 0, 1);
    }

    if (range_string == "all_time") {
      currentToDate = new Date();
      currentFromDate = new Date(2018, 0, 1);
    }

    fetch_data();
  };
</script>

<style>
  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 2rem;
  }

  .date-range-container {
    background: var(--ds-surface);
    border: 1px solid var(--ds-border);
    border-radius: var(--ds-radius-lg);
    box-shadow: var(--ds-shadow-card);
    padding: 1.5rem;
    margin-bottom: 2rem;
  }

  .date-range-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .date-range-picker {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--ds-text-muted);
  }

  .date-range-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .date-range-button {
    padding: 0.4rem 0.9rem;
    border: 1px solid var(--ds-border-3);
    border-radius: var(--ds-radius-pill);
    background: var(--ds-surface);
    color: var(--ds-text-muted);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .date-range-button:hover {
    background: var(--ds-accent-soft);
    border-color: var(--ds-accent);
    color: var(--ds-accent);
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .stat-card {
    background: var(--ds-surface);
    border: 1px solid var(--ds-border);
    border-radius: var(--ds-radius-lg);
    box-shadow: var(--ds-shadow-card);
    overflow: hidden;
  }

  .stat-card-header {
    padding: 1rem 1.5rem;
    background: var(--ds-surface-3);
    border-bottom: 1px solid var(--ds-border);
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--ds-text-muted);
  }

  .stat-card-body {
    padding: 1.5rem;
    text-align: center;
  }

  .stat-value {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--ds-text);
    line-height: 1;
    margin-bottom: 0.5rem;
  }

  .stat-comparison {
    font-size: 0.875rem;
    color: var(--ds-text-muted);
  }

  .stat-delta {
    font-size: 0.8125rem;
    margin-top: 0.25rem;
  }
  .stat-delta-up {
    color: var(--ds-success);
  }
  .stat-delta-down {
    color: var(--ds-danger);
  }

  .section {
    background: var(--ds-surface);
    border: 1px solid var(--ds-border);
    border-radius: var(--ds-radius-lg);
    box-shadow: var(--ds-shadow-card);
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
  .legacy-section details {
    margin: 0;
  }
  .legacy-section summary {
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    color: var(--ds-text);
  }
  .legacy-grid {
    margin-top: 1rem;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--ds-text);
  }

  .section-subtitle {
    font-size: 0.875rem;
    color: var(--ds-text-muted);
  }

  .chart {
    margin-top: 1rem;
  }

  .chart-row {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .chart-label {
    width: 200px;
    font-size: 0.875rem;
    color: var(--ds-text-muted);
    padding-right: 1rem;
  }

  .chart-bar-container {
    flex: 1;
    background: var(--ds-surface-2);
    border-radius: 4px;
    overflow: hidden;
  }

  .chart-bar {
    height: 24px;
    background: var(--ds-accent);
    color: white;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0 0.75rem;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    transition: width 0.3s ease;
  }

  .chart-bar:hover {
    background: var(--ds-accent-hover);
  }

  .loading-card {
    text-align: center;
    padding: 2rem;
  }

  .spinner {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    border: 3px solid var(--ds-accent-soft);
    border-radius: 50%;
    border-top-color: var(--ds-accent);
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-message {
    text-align: center;
    padding: 2rem;
    color: var(--ds-danger);
    font-weight: 500;
  }

  @media (max-width: 768px) {
    .container {
      padding: 1rem;
    }

    .date-range-container {
      padding: 1rem;
    }

    .date-range-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .date-range-buttons {
      width: 100%;
    }

    .date-range-button {
      flex: 1;
      text-align: center;
    }

    .stats-grid {
      grid-template-columns: 1fr;
    }

    .chart-label {
      width: 120px;
      font-size: 0.75rem;
    }
  }
</style>

{#await fetch_data()}
  <div class="container">
    <div class="ds-page-header">
      <h1 class="ds-page-title">Analytics</h1>
    </div>
    <div class="date-range-container">
      <div class="loading-card">
        <div class="spinner"></div>
        <div>Loading dashboard data...</div>
      </div>
    </div>
  </div>
{:then data}
  <div class="container">
    <div class="ds-page-header">
      <h1 class="ds-page-title">Analytics</h1>
    </div>
    <div class="date-range-container">
      <div class="date-range-header">
        <div class="date-range-picker">
          From <DatePicker
            on:datechange={onFromDateChange}
            selected={currentFromDate}
            isAllowed={(date) => {
              const millisecs = date.getTime();
              if (millisecs > Date.now() + 3600 * 24 * 45 * 1000) return false;
              return true;
            }}
          />
          to <DatePicker
            on:datechange={onToDateChange}
            selected={currentToDate}
            isAllowed={(date) => {
              const millisecs = date.getTime();
              if (millisecs > Date.now() + 3600 * 24 * 45 * 1000) return false;
              return true;
            }}
          />
        </div>
      </div>
      <div class="date-range-buttons">
        <button class="date-range-button" on:click={() => set_date_range("last_7_days")}>
          Last 7 days
        </button>
        <button class="date-range-button" on:click={() => set_date_range("last_28_days")}>
          Last 4 weeks
        </button>
        <button class="date-range-button" on:click={() => set_date_range("last_90_days")}>
          Last quarter
        </button>
        <button class="date-range-button" on:click={() => set_date_range("last_365_days")}>
          Last year
        </button>
        <button class="date-range-button" on:click={() => set_date_range("year_to_date")}>
          This year
        </button>
        <button class="date-range-button" on:click={() => set_date_range("all_time")}>
          All time
        </button>
      </div>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-card-header">Total Number of Sign Ups</div>
        <div class="stat-card-body">
          <div class="stat-value" title="from {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}">
            {dashboard_summary.signup_count}
          </div>
          <div class="stat-comparison" title="from {new Date(dashboard_summary.meta.previous_from).toDateString()} to {new Date(dashboard_summary.meta.previous_to).toDateString()}">
            Previous period: {dashboard_summary.previous_signup_count}
          </div>
          <div class="stat-delta" class:stat-delta-up={signupDelta > 0} class:stat-delta-down={signupDelta < 0}>
            {signupDelta > 0 ? '+' : ''}{signupDelta.toFixed(1)}% vs previous period
          </div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-card-header">Total Number of Active Users</div>
        <div class="stat-card-body">
          <div class="stat-value" title="from {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}">
            {dashboard_summary.mau_count}
          </div>
          <div class="stat-comparison" title="from {new Date(dashboard_summary.meta.previous_from).toDateString()} to {new Date(dashboard_summary.meta.previous_to).toDateString()}">
            Previous period: {dashboard_summary.previous_mau_count}
          </div>
          <div class="stat-delta" class:stat-delta-up={mauDelta > 0} class:stat-delta-down={mauDelta < 0}>
            {mauDelta > 0 ? '+' : ''}{mauDelta.toFixed(1)}% vs previous period
          </div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-card-header">Starter</div>
        <div class="stat-card-body">
          <div class="stat-value" title="from {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}">
            {starterCount}
          </div>
          <div class="stat-comparison" title="from {new Date(dashboard_summary.meta.previous_from).toDateString()} to {new Date(dashboard_summary.meta.previous_to).toDateString()}">
            Previous period: {previousStarterCount}
          </div>
          <div class="stat-delta" class:stat-delta-up={starterDelta > 0} class:stat-delta-down={starterDelta < 0}>
            {starterDelta > 0 ? '+' : ''}{starterDelta.toFixed(1)}% vs previous period
          </div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-card-header">Professional Trial</div>
        <div class="stat-card-body">
          <div class="stat-value" title="from {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}">
            {dashboard_summary.workspace_trial_count ?? 0}
          </div>
          <div class="stat-comparison" title="from {new Date(dashboard_summary.meta.previous_from).toDateString()} to {new Date(dashboard_summary.meta.previous_to).toDateString()}">
            Previous period: {dashboard_summary.previous_workspace_trial_count ?? 0}
          </div>
          <div class="stat-delta" class:stat-delta-up={trialDelta > 0} class:stat-delta-down={trialDelta < 0}>
            {trialDelta > 0 ? '+' : ''}{trialDelta.toFixed(1)}% vs previous period
          </div>
        </div>
      </div>

      {#each dashboard_summary.workspace_subscription_counts as workspace_plan_count}
        <div class="stat-card">
          <div class="stat-card-header">{getPlanDisplayName(workspace_plan_count.plan_title, workspace_plan_count.plan_id)}</div>
          <div class="stat-card-body">
            <div class="stat-value" title="from {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}">
              {workspace_plan_count.count}
            </div>
            <div class="stat-comparison">Previous period: {getPlanPeriodDelta(dashboard_summary, workspace_plan_count).prevCount}</div>
            <div class="stat-delta" class:stat-delta-up={getPlanPeriodDelta(dashboard_summary, workspace_plan_count).delta > 0} class:stat-delta-down={getPlanPeriodDelta(dashboard_summary, workspace_plan_count).delta < 0}>
              {getPlanPeriodDelta(dashboard_summary, workspace_plan_count).delta > 0 ? '+' : ''}{getPlanPeriodDelta(dashboard_summary, workspace_plan_count).delta.toFixed(1)}% vs previous period
            </div>
          </div>
        </div>
      {/each}

      <div class="stat-card">
        <div class="stat-card-header">Single device users</div>
        <div class="stat-card-body">
          <div class="stat-value" title="from {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}">
            {dashboard_summary.single_device_count}
          </div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-card-header">Multi device users</div>
        <div class="stat-card-body">
          <div class="stat-value" title="from {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}">
            {dashboard_summary.multi_device_count}
          </div>
        </div>
      </div>
    </div>

    <div class="section legacy-section">
      <details>
        <summary class="section-title">Legacy user plans</summary>
        <div class="stats-grid legacy-grid">
          <div class="stat-card">
            <div class="stat-card-header">Pro 50</div>
            <div class="stat-card-body">
              <div class="stat-value">{dashboard_summary.pro_50_count ?? 0}</div>
              <div class="stat-comparison">Previous: {dashboard_summary.previous_pro_50_count ?? 0}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-card-header">Pro 200</div>
            <div class="stat-card-body">
              <div class="stat-value">{dashboard_summary.pro_200_count ?? 0}</div>
              <div class="stat-comparison">Previous: {dashboard_summary.previous_pro_200_count ?? 0}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-card-header">Pro 2000</div>
            <div class="stat-card-body">
              <div class="stat-value">{dashboard_summary.pro_2000_count ?? 0}</div>
              <div class="stat-comparison">Previous: {dashboard_summary.previous_pro_2000_count ?? 0}</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-card-header">Pro Max</div>
            <div class="stat-card-body">
              <div class="stat-value">{dashboard_summary.pro_max_count ?? 0}</div>
              <div class="stat-comparison">Previous: {dashboard_summary.previous_pro_max_count ?? 0}</div>
            </div>
          </div>
        </div>
      </details>
    </div>

    <div class="section">
      <div class="section-header">
        <div>
          <h2 class="section-title">Platform Login Breakdown</h2>
          <div class="section-subtitle">
            From {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}
          </div>
        </div>
      </div>
      <div class="chart">
        {#each dashboard_summary.platform_list_counts as item}
          <div class="chart-row">
            <div class="chart-label">{item.platform_list}</div>
            <div class="chart-bar-container">
              <div
                class="chart-bar"
                style="width: {max_platform_occurence ? (item.platform_list_count / max_platform_occurence) * 100 : 0}%"
                title="{item.platform_list_count} users">
                {item.platform_list_count}
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>

    <div class="section">
      <div class="section-header">
        <div>
          <h2 class="section-title">Platform OS Breakdown</h2>
          <div class="section-subtitle">
            From {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}
          </div>
        </div>
      </div>
      <div class="chart">
        {#each dashboard_summary.platform_os_counts as item}
          <div class="chart-row">
            <div class="chart-label">
              {item.device_platform} {item.device_os_version}
            </div>
            <div class="chart-bar-container">
              <div
                class="chart-bar"
                style="width: {max_platform_os_occurence ? (item.count / max_platform_os_occurence) * 100 : 0}%"
                title="{item.count} users">
                {item.count}
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>

    <div class="section">
      <div class="section-header">
        <div>
          <h2 class="section-title">Daily Signups</h2>
          <div class="section-subtitle">
            From {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}
          </div>
        </div>
      </div>
      <div class="chart">
        {#each signup_data.records as data_point}
          <div class="chart-row">
            <div class="chart-label">{data_point.formatted_date}</div>
            <div class="chart-bar-container">
              <div
                class="chart-bar"
                style="width: {(data_point.count / max_signup) * 100}%"
                title="{data_point.count} signups">
                {data_point.count}
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>

    <div class="section">
      <div class="section-header">
        <div>
          <h2 class="section-title">Daily Logins</h2>
          <div class="section-subtitle">
            From {new Date(dashboard_summary.meta.from).toDateString()} to {new Date(dashboard_summary.meta.to).toDateString()}
          </div>
        </div>
      </div>
      <div class="chart">
        {#each login_data.records as data_point}
          <div class="chart-row">
            <div class="chart-label">{data_point.formatted_date}</div>
            <div class="chart-bar-container">
              <div
                class="chart-bar"
                style="width: {(data_point.count / max_login) * 100}%"
                title="{data_point.count} logins">
                {data_point.count}
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </div>
{:catch error}
  <div class="container">
    <div class="ds-page-header">
      <h1 class="ds-page-title">Analytics</h1>
    </div>
    <div class="date-range-container">
      <div class="error-message">
        {error?.message || "Failed to load dashboard data. Please try again later."}
      </div>
    </div>
  </div>
{/await}
